import { useAtom } from "@dbeining/react-atom";
import { ErrorMessage, Formik } from "formik";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import utc from "dayjs/plugin/utc";
import { useEffect, useRef, useState } from "react";
import {
    Alert, Button, Col, Container, Form, InputGroup, Row,
    Stack
} from "react-bootstrap";
import { Helmet } from "react-helmet";
import ReactQuill from "react-quill";
import { useLocation } from "react-router-dom";
import * as Yup from "yup";
import { userAtom } from "../../atoms";
import { LoadingSpinner } from "../../components/common/Loading/LoadingSpinner";
import { EditFormActions } from "../../components/Forms";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";
import {
    EMAIL_TEMPLATE_PLACEHOLDERS
} from "../../constants/email/EmailTemplate";
import { emailTemplateService } from "../../services";
import type { IEmailTemplateDetail } from "../../customTypings/email/EmailTemplateDetail";
import CustomDropDown from "../../components/common/Custom/CustomDropDown";
import CustomField from "../../components/common/Custom/CustomField";
import TemplatePage from "./TemplatePage";
import { createEditFormSubmitHandler, getUserFacingErrorMessage, logErrorForDev, showToastError, showToastSuccess, appRoutes } from "../../utils";

dayjs.extend(advancedFormat);
dayjs.extend(utc);

function formatMetaDate(iso: string | null | undefined): string {
    if (!iso) return "";
    return dayjs.utc(iso).local().format("Do MMMM YYYY [at] HH:mm");
}

function formatByLine(name: string | null | undefined): string {
    const t = typeof name === "string" ? name.trim() : "";
    return t.length > 0 ? t : "Unknown";
}

const EmailTemplateEditPage: React.FC = () => {
    const [alertVariant, setAlertVariant] = useState("danger");
    const [detail, setDetail] = useState<IEmailTemplateDetail | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);

    const { state } = useLocation();
    const quillRef = useRef<any>();
    const labelSpan = 3;
    const fieldSpan = 9;
    const item = state.item;
    const title: string = state?.item?.name;
    const placeholders = EMAIL_TEMPLATE_PLACEHOLDERS[title] ?? {};
    const placeholderKeys = Object.keys(placeholders);
    const currentUser = useAtom(userAtom);
    const templateAttributes = (state?.templateAttributes ?? []).filter(
        (attribute: string) => attribute !== "attachPDFForm"
    );

    useEffect(() => {
        const id = item?.id;
        if (!id) {
            setDetail(null);
            setLoadError(null);
            return;
        }
        setLoadError(null);
        emailTemplateService
            .getDetail(id)
            .then((d) => {
                setDetail(d as IEmailTemplateDetail);
                setLoadError(null);
            })
            .catch((e: unknown) => {
                logErrorForDev("EmailTemplateEditPage.load", e);
                setDetail(null);
                setLoadError(getUserFacingErrorMessage(e, "Could not load email template."));
            });
    }, [item?.id]);

return (
    <Container fluid>
        <Helmet>
            <title> {title}</title>
        </Helmet>

        <ResponsiveBreadcrumb
            items={[
                { text: "Settings", to: "/settings" },
                { text: "Automatic Emails", to: appRoutes.automaticEmails },
                { text: title ?? "Template", labelNode: title },
            ]}
        />
        {loadError && (
            <Alert variant="danger" className="mb-3">
                {loadError}
            </Alert>
        )}
        {detail && (
            <div className="d-flex flex-wrap align-items-start gap-4 mb-3">
                <div className="d-flex flex-wrap gap-4 gap-md-5 align-items-start">
                    {detail.createdAtUtc && (
                        <div className="d-flex text-start align-items-start" style={{ minWidth: "11rem" }}>
                            <span className="text-muted text-nowrap me-2">Created:</span>
                            <div>
                                <div>{formatMetaDate(detail.createdAtUtc)}</div>
                                <div className="text-muted small">by {formatByLine(detail.createdByDisplayName)}</div>
                            </div>
                        </div>
                    )}
                    {(detail.lastModifiedTime || detail.latestAuditActionAtUtc) && (
                        <div className="d-flex text-start align-items-start" style={{ minWidth: "11rem" }}>
                            <span className="text-muted text-nowrap me-2">Last updated:</span>
                            <div>
                                <div>
                                    {formatMetaDate(detail.lastModifiedTime ?? detail.latestAuditActionAtUtc ?? undefined)}
                                </div>
                                <div className="text-muted small">
                                    by {formatByLine(detail.lastUpdatedByDisplayName ?? detail.lastModifiedBy)}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="ms-auto d-flex flex-wrap gap-4 align-items-start">
                    {detail.isDeleted && (detail.deletedAuditAtUtc || detail.lastModifiedTime) && (
                        <div className="d-flex text-start align-items-start" style={{ minWidth: "11rem" }}>
                            <span className="text-danger text-nowrap me-2">Deleted:</span>
                            <div>
                                <div>
                                    {formatMetaDate(
                                        detail.deletedAuditAtUtc ??
                                            detail.latestAuditActionAtUtc ??
                                            detail.lastModifiedTime
                                    )}
                                </div>
                                <div className="text-muted small">by {formatByLine(detail.deletedByDisplayName)}</div>
                            </div>
                        </div>
                    )}
                    {!detail.isDeleted && !detail.enabled && (detail.disabledAuditAtUtc || detail.lastModifiedTime) && (
                        <div className="d-flex text-start align-items-start" style={{ minWidth: "11rem" }}>
                            <span className="text-warning text-nowrap me-2">Disabled:</span>
                            <div>
                                <div>
                                    {formatMetaDate(
                                        detail.disabledAuditAtUtc ??
                                            detail.latestAuditActionAtUtc ??
                                            detail.lastModifiedTime
                                    )}
                                </div>
                                <div className="text-muted small">by {formatByLine(detail.disabledByDisplayName)}</div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        )}
        <>
            <Formik
                initialValues={item}
                validationSchema={Yup.object().shape({
                    name: Yup.string().required("Field is required"),
                    subject: Yup.string().required("Field is required"),
                    body: Yup.string().required("Field is required"),
                })}
                onSubmit={(
                    values,
                    { setStatus, setSubmitting, setFieldError, resetForm }
                ) => {
                    setStatus();
                    setSubmitting(false);
                    emailTemplateService
                        .edit({
                            ...values,
                            lastModifiedBy: currentUser?.identity.displayName,
                            lastModifiedTime: new Date(),
                        })
                        .then(
                            (response) => {
                                setSubmitting(false);
                                if (response.status !== "Failure") {
                                    setAlertVariant("success");
                                    resetForm();
                                    showToastSuccess("Template updated successfully!");
                                    const tid = item?.id;
                                    if (tid) {
                                        emailTemplateService
                                            .getDetail(tid)
                                            .then((d) => setDetail(d as IEmailTemplateDetail))
                                            .catch(() => {});
                                    }
                                } else {
                                    setAlertVariant("danger");
                                    showToastError(response.message);
                                }
                            },
                            (error: any) => {
                                setAlertVariant("danger");
                                if (error.status === 400 && error.errors) {
                                    Object.keys(error.errors).forEach((key) => {
                                        const messages = error.errors[key];
                                        if (Array.isArray(messages)) {
                                            messages.forEach((message) => showToastError(message));
                                        }
                                    });
                                } else {
                                    showToastError("Something went wrong. Please try again.");
                                }
                                setSubmitting(false);
                            }
                        );
                }}
            >
                {({
                    values,
                    errors,
                    status,
                    touched,
                    isSubmitting,
                    handleSubmit,
                    handleChange,
                    setStatus,
                    setFieldValue,
                    validateForm,
                    setTouched,
                }) => (
                    <Form
                        noValidate
                        onSubmit={createEditFormSubmitHandler(validateForm, setTouched, values, handleSubmit)}
                    >
                        <div className="d-flex justify-content-between">
                            <h1 className="my-3">{title}</h1>

                            <div className="d-flex">
                                <p className="me-4"> Enabled</p>
                                <div className="form-check form-switch">
                                    <input
                                        name="enabled"
                                        onChange={handleChange}
                                        checked={values.enabled}
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                    />
                                </div>
                            </div>
                        </div>
                        <Stack gap={3}>
                            <Form.Group as={Row} controlId="subject">
                                <Form.Label column xs={6} lg={6} xl={labelSpan - 2}>
                                    Subject <span style={{ color: 'red' }}>*</span>
                                </Form.Label>
                                <Col xs={12} lg={12} xl={fieldSpan}>
                                    <CustomField
                                        name="subject"
                                        type="text"
                                        handleChange={handleChange}
                                        values={values}
                                        errors={errors}
                                        touched={touched}
                                    />
                                </Col>
                            </Form.Group>

                            <Form.Group as={Row} controlId="body">
                                <Form.Label column xs={6} lg={6} xl={labelSpan - 2}>
                                    Body <span style={{ color: 'red' }}>*</span>
                                </Form.Label>
                                <Col xs={6} lg={6} xl={2} align="end">
                                    {placeholderKeys.length > 0 && (
                                        <CustomDropDown
                                            title="Insert Placeholders"
                                            placeholders={placeholderKeys}
                                            setFieldValue={setFieldValue}
                                            values={values}
                                            clickHandler={(value: string) => {
                                                value && setFieldValue("body", values.body + value);
                                            }}
                                        />
                                    )}
                                </Col>
                                <Col xs={12} lg={12} xl={fieldSpan}>
                                    <InputGroup hasValidation>
                                        <ReactQuill
                                            ref={quillRef}
                                            value={values.body ?? ""}
                                            onChange={(value, delta, source, editor) => {
                                                setFieldValue("body", value);
                                                setFieldValue(
                                                    "bodyCursorLocation",
                                                    editor.getSelection(true).index ?? 0
                                                );
                                            }}
                                            onBlur={(previousSelection, source, editor) => {
                                                setFieldValue(
                                                    "bodyCursorLocation",
                                                    previousSelection?.index ?? 0
                                                );
                                            }}
                                        />
                                        <ErrorMessage
                                            name="body"
                                            component="div"
                                            className="invalid-feedback"
                                            render={(errorMessage) => (
                                                <Form.Control.Feedback type="invalid">
                                                    {errorMessage}
                                                </Form.Control.Feedback>
                                            )}
                                        />
                                    </InputGroup>
                                </Col>
                            </Form.Group>

                            <TemplatePage
                                values={values}
                                handleChange={handleChange}
                                templateAttributes={templateAttributes}
                            />

                            {status && (
                                <Alert
                                    variant={alertVariant}
                                    className="mt-3"
                                    onClose={() => setStatus()}
                                    dismissible
                                >
                                    {status}
                                </Alert>
                            )}
                            {/* No Cancel: reached from template list via router state; Save-only footer matches admin pattern. */}
                            <EditFormActions>
                                <Button variant="primary" disabled={isSubmitting} type="submit">
                                    {isSubmitting ? <LoadingSpinner text="Saving…" /> : "Save"}
                                </Button>
                            </EditFormActions>
                        </Stack>
                    </Form>
                )}
            </Formik>
        </>
    </Container>
);
};

export default EmailTemplateEditPage;
